import React from "react";
import { IonIcon, IonButton, IonToolbar, IonTitle, IonButtons } from "@ionic/react";
import { cameraOutline } from "ionicons/icons";
import CustomBox from "./CustomBox";
import { Camera, CameraResultType, CameraSource, CameraDirection } from "@capacitor/camera";
import SitePhotosList from "./SitePhotosList";
import { SitePhoto } from "../models/SitePhoto";
import { User } from "../models/User";
import { PermitAffectedService } from "../models/PermitAffectedService";
import AffectedServicePhotos from "./AffectedServicePhotos";
import { SitePhotoType } from "../models/SitePhotoType";
import { SitePhotoTypes } from "../SitePhotoService";

interface Props {
    sitePhotos?: SitePhoto[];
    onSitePhotoCaptured: (dataUrl: string) => void;
    onSitePhotoDeleted: (sitePhotoId: string) => void;
    isReadOnly: boolean;
    permitId: string;
    ableToAddPhotos: boolean;
    editablePhotoTypes: SitePhotoType[];
    permitRegisterUsers?: User[];
    affectedServices: PermitAffectedService[];
    onAffectedServicePhotoCaptured: (dataUrl: string, affectedService: PermitAffectedService, comments: string) => void;
    onAffectedServiceNoPhotoReason: (affectedService: PermitAffectedService, reason: string) => void;
}

const SitePhotos: React.FC<Props> = ({
    sitePhotos,
    permitId,
    onSitePhotoCaptured,
    onSitePhotoDeleted,
    isReadOnly,
    ableToAddPhotos,
    editablePhotoTypes,
    permitRegisterUsers,
    affectedServices,
    onAffectedServicePhotoCaptured,
    onAffectedServiceNoPhotoReason,
}) => {
    const takePicture = async () => {
        try {
            const image = await Camera.getPhoto({
                resultType: CameraResultType.DataUrl,
                source: CameraSource.Camera,
                direction: CameraDirection.Rear,
                quality: 80,
            });

            if (image.dataUrl) {
                onSitePhotoCaptured(image.dataUrl);
            }
        } catch (error) {
            console.error("Error picking photo:", error);
        }
    };

    return (
        <>
            <CustomBox>
                <IonToolbar mode="md">
                    <IonTitle style={{ padding: "0px 0px 0px 16px" }} class="ion-text-uppercase " size="small">
                        Site photos
                    </IonTitle>
                    {ableToAddPhotos && !(sitePhotos && sitePhotos.length > 15) && (
                        <IonButtons slot="end">
                            <IonButton onClick={() => takePicture()} color="medium" size="small">
                                <IonIcon slot="start" icon={cameraOutline} />
                                Take Photo
                            </IonButton>
                        </IonButtons>
                    )}
                </IonToolbar>

                <SitePhotosList
                    permitId={permitId}
                    sitePhotos={sitePhotos?.filter((sp) => !sp.affectedService)}
                    onSitePhotoDeleted={onSitePhotoDeleted}
                    isReadOnly={isReadOnly}
                    editablePhotoTypes={editablePhotoTypes}
                    permitRegisterUsers={permitRegisterUsers}
                />
            </CustomBox>

            <CustomBox
                title="Affected services mark up photos"
                text="A photo must be taken for each affected service within the permit boundary. If a photo cannot be taken, a reason must be provided."
            >
                <AffectedServicePhotos
                    affectedServices={affectedServices}
                    sitePhotos={sitePhotos?.filter((sp) => !!sp.affectedService)}
                    permitId={permitId}
                    isReadOnly={isReadOnly || !editablePhotoTypes.some((pt) => pt.key === SitePhotoTypes.Issue.key)}
                    ableToAddPhotos={ableToAddPhotos}
                    editablePhotoTypes={editablePhotoTypes}
                    onAffectedServicePhotoCaptured={onAffectedServicePhotoCaptured}
                    onAffectedServiceNoPhotoReason={onAffectedServiceNoPhotoReason}
                    onSitePhotoDeleted={onSitePhotoDeleted}
                />
            </CustomBox>
        </>
    );
};

export default SitePhotos;
